// src/idempotency-key-services/parse-idempotency-key.ts
import { raiseIfUnexpectedSize } from "@vy/array";
function parseIdempotencyKey(idempotencyKey) {
  const idempotencyKeyParts = idempotencyKey.split("-");
  raiseIfUnexpectedSize(idempotencyKeyParts, 6, "idempotencyKey");
  const [_vayuPrefix, accountId, customerId, invoiceId, lineItemId, exportTimestamp] = idempotencyKeyParts;
  return {
    accountId,
    customerId,
    invoiceId,
    lineItemId,
    exportTimestamp
  };
}

// src/idempotency-key-services/idempotency-key-creator.ts
function createIdempotencyKey({
  accountId,
  customerId,
  invoiceId,
  lineItemId,
  exportTimestamp
}) {
  return `vayu-${accountId}-${customerId}-${invoiceId}-${lineItemId}-${exportTimestamp}`;
}

// src/cloud-provider/cloud-provider-types/cloud-provider.ts
import { oz } from "@vy/oz";
var CLOUD_PROVIDER = Object.freeze({
  AWS: "AWS"
});
var CloudProviderSchema = oz.nativeEnum(CLOUD_PROVIDER);

// src/auth-connect/auth-connect-types/connact-auth-data.ts
import { oz as oz2 } from "@vy/oz";
var ConnactAuthDataSchema = oz2.object({
  accessToken: oz2.string()
});

// src/marketplace-units/compute-marketplace-units.ts
import { isEmpty } from "@vy/array";
import { getOptionalObjectProperty } from "@vy/object";
import { raiseUnreachable } from "@vy/err";

// src/marketplace-units/errors/marketplace-units-errors.ts
var MARKETPLACE_UNITS_ERRORS = Object.freeze({
  UNEXPECTED_ZESTY_DIMENSION: "UNEXPECTED_ZESTY_DIMENSION"
});

// src/marketplace-units/errors/unexpected-zesty-dimension-error.ts
import { UnreachableValueError } from "@vy/err";
var UnexpectedZestyDimensionError = class extends UnreachableValueError {
  constructor(_dimension) {
    super("Zesty dimension", MARKETPLACE_UNITS_ERRORS.UNEXPECTED_ZESTY_DIMENSION);
  }
};

// src/marketplace-units/constants/zesty-constants.ts
var ZESTY_ACCOUNT_ID = "685b9c1660636cb3eff7c636";

// src/marketplace-units/types/zesty-dimension-types.ts
import { oz as oz3 } from "@vy/oz";
var ZESTY_DIMENSION = Object.freeze({
  DOLLAR_CM_FEE: "cm_dollar_fee",
  CM_FEE: "cm_fee",
  OTHER: "other"
  // ZestyDisk dimension name
});
var ZestyDimensionSchema = oz3.nativeEnum(ZESTY_DIMENSION);

// src/marketplace-units/compute-marketplace-units.ts
function computeMarketplaceUnits(lineItems, accountId) {
  const eligibleLineItems = lineItems.filter(isMarketplaceEligible);
  if (eligibleLineItems.length === 0) {
    return void 0;
  }
  if (accountId === ZESTY_ACCOUNT_ID) {
    return computeZestyUnits(eligibleLineItems);
  }
  return computeGeneralUnits(eligibleLineItems);
}
function isMarketplaceEligible(lineItem) {
  const settings = lineItem.lineItemProduct.cloudProviderSettings;
  return settings?.dimension != null && settings?.cloudProviderMarketplaceId != null;
}
function computeGeneralUnits(lineItems) {
  const quantityByDimension = lineItems.reduce(
    (quantityByDimensionAccumulator, lineItem) => {
      const dimension = lineItem.lineItemProduct.cloudProviderSettings.dimension;
      const existingDimensionQuantity = getOptionalObjectProperty(quantityByDimensionAccumulator, dimension) ?? 0;
      quantityByDimensionAccumulator[dimension] = existingDimensionQuantity + (lineItem.units ?? 0);
      return quantityByDimensionAccumulator;
    },
    {}
  );
  const dimensions = Object.entries(quantityByDimension).map(
    ([dimension, quantity]) => ({
      dimension,
      quantity: Math.floor(quantity)
    })
  );
  const total = dimensions.reduce((sum, entry) => sum + entry.quantity, 0);
  return { total, dimensions };
}
function isUsageLineItem(lineItem) {
  return lineItem.type === "Usage" || lineItem.type === "Commitment";
}
function computeZestyUnits(lineItems) {
  const usageLineItems = lineItems.filter(isUsageLineItem);
  const feeRate = findZestyFeeRate(lineItems);
  const quantityByDimension = usageLineItems.reduce(
    (quantityByDimensionAccumulator, lineItem) => {
      const basePrice = lineItem.lineItemProduct.basePrice ?? 0;
      const dimension = ZestyDimensionSchema.parse(lineItem.lineItemProduct.cloudProviderSettings.dimension);
      const variableAmount = Math.max(0, lineItem.revenueBreakdown.total - basePrice);
      const existingDimensionQuantity = getOptionalObjectProperty(quantityByDimensionAccumulator, dimension) ?? 0;
      const zestyQuantity = computeZestyQuantityByDimension(variableAmount, dimension, feeRate);
      quantityByDimensionAccumulator[dimension] = existingDimensionQuantity + zestyQuantity;
      return quantityByDimensionAccumulator;
    },
    {}
  );
  const dimensions = Object.entries(quantityByDimension).map(
    ([dimension, quantity]) => ({
      dimension,
      quantity: Math.floor(quantity)
    })
  );
  const totalQuantity = dimensions.reduce((total, entry) => total + entry.quantity, 0);
  return { total: totalQuantity, dimensions };
}
function computeZestyQuantityByDimension(variableAmount, dimension, feeRate) {
  switch (dimension) {
    case ZESTY_DIMENSION.DOLLAR_CM_FEE:
    case ZESTY_DIMENSION.OTHER:
      return variableAmount;
    case ZESTY_DIMENSION.CM_FEE:
      if (feeRate <= 0) {
        return 0;
      }
      return variableAmount / (feeRate / 100);
    default:
      return raiseUnreachable(dimension, new UnexpectedZestyDimensionError(dimension));
  }
}
function findZestyFeeRate(lineItems) {
  for (const lineItem of lineItems) {
    const variants = lineItem.lineItemProduct.variants;
    const tieredVariants = variants.filter((variant) => variant.pricing.pricingModel === "Tiers");
    const revShareVariant = tieredVariants.find((variant) => variant.pricing.priceConfiguration.isRevShare);
    const tiers = revShareVariant?.pricing.priceConfiguration.tiers ?? [];
    if (!isEmpty(tiers)) {
      return tiers[0].price;
    }
  }
  return 0;
}
export {
  CLOUD_PROVIDER,
  CloudProviderSchema,
  ConnactAuthDataSchema,
  MARKETPLACE_UNITS_ERRORS,
  UnexpectedZestyDimensionError,
  ZESTY_ACCOUNT_ID,
  ZESTY_DIMENSION,
  ZestyDimensionSchema,
  computeMarketplaceUnits,
  computeZestyQuantityByDimension,
  createIdempotencyKey,
  parseIdempotencyKey
};
